import React, { useState } from 'react';
import axios from 'axios';
import { Search, Shield, Mail, UserCheck, KeyRound, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { getApiUrl } from '../../utils/authUtils';

const API = getApiUrl();

const STATUS_LABELS = {
  active_password_login_ok: 'Can log in (active + password)',
  inactive_account: 'Inactive — blocked from login',
  no_password_oauth_only: 'OAuth only — no password',
  missing_email: 'Missing email',
  duplicate_email_possible: 'Possible duplicate email',
};

const UserLoginAccessPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [access, setAccess] = useState(null);

  const canManage = ['platform_owner', 'platform_admin', 'super_admin'].includes(user?.role);

  const searchUser = async () => {
    if (!email.trim()) {
      toast.error('Enter an email to search');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin-ops/users/login-access/search`, {
        params: { email: email.trim().toLowerCase() },
        headers: getAuthHeaders(),
      });
      setAccess(response.data.access);
      if (!response.data.access?.found) {
        toast.message('No user found for that email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
      setAccess(null);
    } finally {
      setLoading(false);
    }
  };

  const activateAccount = async () => {
    if (!access?.user_id) return;
    setActionLoading('activate');
    try {
      await axios.post(
        `${API}/admin-ops/users/${access.user_id}/activate`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Account activated');
      await searchUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Activation failed');
    } finally {
      setActionLoading('');
    }
  };

  const sendPasswordReset = async () => {
    if (!access?.user_id) return;
    setActionLoading('reset');
    try {
      const response = await axios.post(
        `${API}/admin-ops/users/${access.user_id}/send-password-reset`,
        {},
        { headers: getAuthHeaders() }
      );
      const sent = response.data.email_sent;
      toast.success(
        sent
          ? 'Password reset email sent'
          : 'Reset token created — email provider may not be configured. Use Force reset link or CLI script.'
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset');
    } finally {
      setActionLoading('');
    }
  };

  const forcePasswordReset = async () => {
    if (!access?.user_id) return;
    setActionLoading('force');
    try {
      const response = await axios.post(
        `${API}/admin-ops/users/${access.user_id}/force-password-reset`,
        {},
        { headers: getAuthHeaders() }
      );
      const url = response.data.reset_url;
      if (url && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        toast.success('Reset link copied to clipboard (expires in 60 minutes)');
      } else {
        toast.success(`Reset link: ${url}`, { duration: 15000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate reset link');
    } finally {
      setActionLoading('');
    }
  };

  if (!canManage) {
    return (
      <div className="p-8 text-center text-slate-600">
        Platform admin access is required to manage user login access.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-emerald-600" />
          User Login Access
        </h1>
        <p className="text-slate-600 mt-1">
          Search by email, review login status, activate accounts, or send password reset links.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Search user</CardTitle>
          <CardDescription>Case-insensitive email lookup</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              className="pl-9"
              placeholder="user@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUser()}
            />
          </div>
          <Button onClick={searchUser} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
          </Button>
        </CardContent>
      </Card>

      {access?.found && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className="h-5 w-5" />
              {access.name || access.email}
            </CardTitle>
            <CardDescription>{access.email}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">User ID</span>
                <p className="font-mono text-xs mt-0.5">{access.user_id}</p>
              </div>
              <div>
                <span className="text-slate-500">Role</span>
                <p>{access.role || '—'}</p>
              </div>
              <div>
                <span className="text-slate-500">Active</span>
                <p>
                  <Badge variant={access.is_active ? 'default' : 'destructive'}>
                    {access.is_active ? 'Yes' : 'No'}
                  </Badge>
                </p>
              </div>
              <div>
                <span className="text-slate-500">Has password</span>
                <p>{access.has_password_hash ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <span className="text-slate-500">OAuth provider</span>
                <p>{access.has_oauth_provider ? 'Yes' : 'No'}</p>
              </div>
              <div className="col-span-2">
                <span className="text-slate-500">Login status</span>
                <p className="font-medium">
                  {STATUS_LABELS[access.likely_login_status] || access.likely_login_status}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {!access.is_active && (
                <Button
                  variant="outline"
                  onClick={activateAccount}
                  disabled={actionLoading === 'activate'}
                >
                  {actionLoading === 'activate' ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UserCheck className="h-4 w-4 mr-2" />
                  )}
                  Activate account
                </Button>
              )}
              <Button
                variant="outline"
                onClick={sendPasswordReset}
                disabled={actionLoading === 'reset'}
              >
                {actionLoading === 'reset' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Send password reset
              </Button>
              <Button
                variant="outline"
                onClick={forcePasswordReset}
                disabled={actionLoading === 'force'}
              >
                {actionLoading === 'force' ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Force password reset
              </Button>
              <Button
                variant="ghost"
                className="text-slate-600"
                onClick={() =>
                  toast.message(
                    'CLI manual reset: python3 backend/scripts/reset_user_password.py ' +
                      access.email +
                      ' --activate'
                  )
                }
              >
                Manual reset (CLI)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default UserLoginAccessPage;
